import type { DocStorePort, DocTxOps } from '@djgg/core';
import { deriveCompositionLineageV1, validateCompositionLineageV1, validateCompositionRevisionV1, type CompositionLineageV1,
  type CompositionRevisionV1 } from '../compositionLineage.js';
import { buildJobDoc, creationDoc, versionDoc } from '../paths.js';
import { validateAcceptedCompositionV1 } from '../spec/gameSpec.js';
import type { BuildJob, Creation, CreationVersion } from '../types.js';
import { AssetRejectionsRepo } from './assetRejections.js';
import { validateCompositionRoute } from './compositionRoute.js';

export interface CompositionJobLocks {
  targetJobId: string;
  job: BuildJob;
  lineage?: CompositionLineageV1;
  predecessorJob?: BuildJob;
  /** A composed revision's authority: the art it carries binds variants in
   * the namespaces its predecessor published them from. */
  revision?: CompositionRevisionV1;
}

export interface CompositionMediaPolicyLocks {
  revisions: ReadonlyMap<string, number>;
  creations: ReadonlyMap<string, Creation>;
  policy: AssetRejectionsRepo;
  jobs: CompositionJobLocks;
}

export interface CompositionVersionLocks {
  target: CreationVersion;
  predecessor?: CreationVersion;
}

/** Lock target and predecessor jobs in document-path order. The hint only
 * enumerates the digest-bound predecessor path; locked records are validated
 * again before any write. */
export async function lockCompositionJobs(tx: DocTxOps, targetJobId: string,
  hint: CreationVersion): Promise<CompositionJobLocks> {
  const authority = await validateAcceptedCompositionV1(hint.executionPolicy?.composition);
  const lineage = hint.executionPolicy?.compositionLineage
    ? await validateCompositionLineageV1(hint.executionPolicy.compositionLineage, authority)
    : undefined;
  const revision = hint.executionPolicy?.compositionRevision
    ? await validateCompositionRevisionV1(hint.executionPolicy.compositionRevision, authority)
    : undefined;
  const rows = new Map<string, BuildJob>();
  for (const id of [...new Set([targetJobId, ...(lineage ? [lineage.predecessor.jobId] : [])])].sort()) {
    const row = await tx.get<BuildJob>(buildJobDoc(id));
    if (row) rows.set(id, row);
  }
  const job = rows.get(targetJobId);
  if (!job) throw new Error('Composition target job unavailable');
  const checked = await validateCompositionRoute(job, hint, targetJobId);
  if (checked.lineage?.digest !== lineage?.digest) throw new Error('Composition lineage changed while locking jobs');
  if (checked.revision?.digest !== revision?.digest) throw new Error('Composition revision changed while locking jobs');
  const predecessorJob = lineage ? rows.get(lineage.predecessor.jobId) : undefined;
  if (lineage && !predecessorJob) throw new Error('Composition predecessor job unavailable');
  return { targetJobId, job, ...(lineage ? { lineage, predecessorJob: predecessorJob! } : {}), ...(revision ? { revision } : {}) };
}

/** Call after locking route jobs and before versions, variants or policy rows. */
export async function lockCompositionMediaPolicies(store: DocStorePort, tx: DocTxOps,
  jobs: CompositionJobLocks): Promise<CompositionMediaPolicyLocks> {
  const namespaces = new Set([jobs.job.creationId]);
  if (jobs.lineage) namespaces.add(jobs.lineage.predecessor.creationId);
  if (jobs.lineage?.media.kind !== undefined && jobs.lineage.media.kind !== 'coded') {
    for (const ref of jobs.lineage.media.refs) namespaces.add(ref.creationId);
  }
  for (const item of jobs.revision?.carry ?? []) namespaces.add(item.from.creationId);
  const revisions = new Map<string, number>();
  const creations = new Map<string, Creation>();
  for (const namespace of [...namespaces].sort()) {
    const creation = await tx.get<Creation>(creationDoc(namespace));
    if (!creation) throw new Error('Composition creation authority unavailable');
    creations.set(namespace, creation);
    revisions.set(namespace, creation.assetPolicyRevision ?? 0);
  }
  return { revisions, creations, policy: new AssetRejectionsRepo(store), jobs };
}

/** Lock target and predecessor versions in path order, validate the target,
 * then reconstruct lineage from exact terminal predecessor records. */
export async function lockCompositionVersions(tx: DocTxOps,
  jobs: CompositionJobLocks): Promise<CompositionVersionLocks> {
  const target = { creationId: jobs.job.creationId, version: jobs.job.version };
  const paths = new Set([versionDoc(target.creationId, target.version)]);
  if (jobs.lineage) paths.add(versionDoc(jobs.lineage.predecessor.creationId, jobs.lineage.predecessor.version));
  const rows = new Map<string, CreationVersion>();
  for (const path of [...paths].sort()) {
    const row = await tx.get<CreationVersion>(path);
    if (row) rows.set(path, row);
  }
  const targetVersion = rows.get(versionDoc(target.creationId, target.version));
  if (!targetVersion) throw new Error('Composition target version unavailable');
  const checked = await validateCompositionRoute(jobs.job, targetVersion, jobs.targetJobId);
  if (checked.lineage?.digest !== jobs.lineage?.digest || checked.revision?.digest !== jobs.revision?.digest) {
    throw new Error('Composition lineage changed while locking versions');
  }
  if (!jobs.lineage) return { target: targetVersion };

  const predecessor = jobs.lineage.predecessor;
  const predecessorVersion = rows.get(versionDoc(predecessor.creationId, predecessor.version));
  if (!predecessorVersion || !jobs.predecessorJob) throw new Error('Composition predecessor version unavailable');
  const mediaRefs = jobs.lineage.media.kind === 'coded' ? undefined : jobs.lineage.media.refs;
  const derived = await deriveCompositionLineageV1({
    kind: jobs.lineage.kind,
    authority: checked.authority,
    sourceCreationId: predecessor.creationId,
    sourceVersion: predecessorVersion,
    sourceJobId: predecessor.jobId,
    sourceJob: jobs.predecessorJob,
    ...(mediaRefs ? { mediaRefs } : {}),
  });
  if (derived.lineage.digest !== jobs.lineage.digest) {
    throw new Error('Composition predecessor authority changed');
  }
  return { target: targetVersion, predecessor: predecessorVersion };
}
